import React, { useEffect, useState, useRef } from 'react'
import axios from 'axios'
import toast from 'react-hot-toast'
import {
  LayoutTemplate, FileText, DollarSign, Radar, Upload, Loader2, Plus, Trash2,
  Save, RotateCcw, Image as ImageIcon, Video, AlertTriangle, CheckCircle2,
  ExternalLink, Sparkles, RefreshCw, ArrowUp, ArrowDown,
  ListChecks, Layers, Check, Minus, HelpCircle, Plug, Shield,
} from 'lucide-react'

// Site Content — admin CMS for the PUBLIC ai.reright.io marketing site (the SSR
// homepage + 14 product pages). Edits content/media overrides and live pricing on
// top of the static catalog (which stays the source of truth + runtime fallback,
// so a blank field or bad override degrades to the built-in default). Every route
// here is server-gated by requireAdmin (ADMIN_EMAILS) → non-admins get a 403.
//
// Endpoints:
//   GET    /api/v1/admin/marketing/pages            → editable pages + current override
//   PUT    /api/v1/admin/marketing/page/:id         → upsert content/media override
//   DELETE /api/v1/admin/marketing/page/:id         → clear override (revert to default)
//   POST   /api/v1/admin/marketing/upload           → multipart `file` → { ok, url }
//   GET    /api/v1/admin/marketing/pricing          → service / current / catalog default
//   PUT    /api/v1/admin/marketing/pricing/:service → set live price { price }
//   POST   /api/v1/admin/marketing/agent/audit      → Site Ops audit (recs + missing media)

const str = (v) => (v == null ? '' : typeof v === 'string' ? v : String(v))
const arr = (v) => (Array.isArray(v) ? v : [])

// Product AND use-case pages share the same field shape, rich-array editors, and
// `{ product }` content envelope — so they get the identical editor surface.
const isRichKind = (kind) => kind === 'product' || kind === 'usecase'

// The override fields this editor exposes as simple scalars. (eyebrow/subhead cover
// the hero copy on home/product/usecase/contact; the contact page's remaining fields
// — email, formTitle, successTitle, … — are agent-managed + API-editable via the
// marketing/page ALLOW list.)
const SCALARS = ['title', 'description', 'eyebrow', 'h1', 'subhead', 'intro', 'heroImage', 'heroVideo', 'heroAlt']

// Normalize a raw showcase tile into the editor's shape, PRESERVING any fields the
// editor doesn't expose (eyebrow, secondaryLabel/secondaryHref, mediaLabel, poster, …)
// so editing one field never drops the rest of a homepage tile.
function tileFromRaw(t) {
  const o = t && typeof t === 'object' ? t : {}
  return {
    ...o,
    id: str(o.id),
    headline: str(o.headline),
    tagline: str(o.tagline),
    ctaLabel: str(o.ctaLabel),
    ctaHref: str(o.ctaHref),
    image: str(o.image),
    video: str(o.video),
  }
}

// A call-to-action button {label, href} — homepage hero (primary/secondary) + product cta.
const ctaFromRaw = (x) => { const o = x && typeof x === 'object' ? x : {}; return { label: str(o.label), href: str(o.href) } }

// A fresh, empty showcase tile with a stable id (used by "Add tile").
const newTile = () => ({
  id: `tile-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`,
  headline: '', tagline: '', ctaLabel: '', ctaHref: '', image: '', video: '',
})

// ── Product-page RICH ARRAY fields (only edited when a `product` page is selected) ──
// These mirror exactly what <ProductPage> renders. Two shapes:
//   • string lists  → whatItIs / howItWorks / useCases / does / doesnt
//   • object lists  → faq {q,a} · integrations {name,note} · security {t,d}
const PRODUCT_STRING_LISTS = ['whatItIs', 'howItWorks', 'useCases', 'does', 'doesnt']
const PRODUCT_OBJECT_LISTS = ['faq', 'integrations', 'security', 'insights']
const PRODUCT_ARRAY_FIELDS = [...PRODUCT_STRING_LISTS, ...PRODUCT_OBJECT_LISTS]
const TRENDS = ['up', 'down', 'flat']

// Display metadata for the plain-text list editors (icon + labels + copy). The
// object lists (faq/integrations/security) are rendered explicitly.
const STRING_LIST_META = {
  whatItIs: { title: 'What it is', icon: FileText, item: 'Paragraph', add: 'Add paragraph',
    blurb: 'The intro paragraphs in the “Meet …” section. One paragraph per item.',
    empty: 'No paragraphs yet — add one, or they’ll load from the live page in a moment.',
    placeholder: 'A short paragraph describing what this service is.' },
  howItWorks: { title: 'How it works', icon: ListChecks, item: 'Step', add: 'Add step',
    blurb: 'The numbered steps under “How it works”. One step per item, in order.',
    empty: 'No steps yet — add one, or they’ll load from the live page in a moment.',
    placeholder: 'e.g. Connect your content source.' },
  useCases: { title: 'Use cases', icon: Layers, item: 'Use case', add: 'Add use case',
    blurb: 'The cards under “Where … earns its keep”. One use case per item.',
    empty: 'No use cases yet — add one, or they’ll load from the live page in a moment.',
    placeholder: 'e.g. Answer repetitive support questions instantly.' },
  does: { title: 'What it does', icon: Check, item: 'Item', add: 'Add item',
    blurb: 'The honest “what it does” list. One clear capability per item.',
    empty: 'Nothing yet — add an item, or they’ll load from the live page in a moment.',
    placeholder: 'e.g. Cites the source for every answer.' },
  doesnt: { title: "What it doesn't do", icon: Minus, item: 'Item', add: 'Add item',
    blurb: 'The honest “what it doesn’t do” list — set expectations plainly.',
    empty: 'Nothing yet — add an item, or they’ll load from the live page in a moment.',
    placeholder: 'e.g. Does not replace human escalation.' },
}

// Normalizers — preserve any unexposed keys on object rows (like tileFromRaw) so
// editing one field never drops the rest of a row.
const faqFromRaw = (x) => { const o = x && typeof x === 'object' ? x : {}; return { ...o, q: str(o.q), a: str(o.a) } }
const intgFromRaw = (x) => { const o = x && typeof x === 'object' ? x : {}; return { ...o, name: str(o.name), note: str(o.note) } }
const secFromRaw = (x) => { const o = x && typeof x === 'object' ? x : {}; return { ...o, t: str(o.t), d: str(o.d) } }
// Insight card {q, a, sources:[], trend}. sources is a string list; trend is an enum.
const insightFromRaw = (x) => {
  const o = x && typeof x === 'object' ? x : {}
  return { ...o, q: str(o.q), a: str(o.a), sources: arr(o.sources).map(str), trend: TRENDS.includes(o.trend) ? o.trend : 'flat' }
}

// Raw → editor shape for one product array field (used by both the override
// hydrate and the live-content seed).
function normalizeProductField(field, raw) {
  if (field === 'faq') return arr(raw).map(faqFromRaw)
  if (field === 'integrations') return arr(raw).map(intgFromRaw)
  if (field === 'security') return arr(raw).map(secFromRaw)
  if (field === 'insights') return arr(raw).map(insightFromRaw)
  return arr(raw).map(str) // string list
}

// A fresh, empty row for each field type.
const newRow = (field) => {
  if (field === 'faq') return { q: '', a: '' }
  if (field === 'integrations') return { name: '', note: '' }
  if (field === 'security') return { t: '', d: '' }
  if (field === 'insights') return { q: '', a: '', sources: [], trend: 'flat' }
  return '' // string list
}

// Decide how ONE cleaned array field lands in the PUT payload, following the
// panel's "don't wipe a default with an empty array" philosophy. The backend
// merge (mergeOverride) ignores undefined/null/'' and falls back to the code
// default — but an explicit [] WINS and blanks the section. So:
//   • items present            → send them (a real override)
//   • empty + already overridden → send [] (a deliberate clear of a saved field)
//   • empty + never overridden   → omit the key → fall back to the built-in default
// `payload` starts life as a spread of `originalOverride`, so "omit" means delete.
function applyArrayField(payload, field, cleaned, originalOverride) {
  if (cleaned.length > 0) payload[field] = cleaned
  else if (Array.isArray(originalOverride[field])) payload[field] = []
  else delete payload[field]
}

// Build the editable form state from a page's stored override.
function formFromOverride(ov) {
  const o = ov && typeof ov === 'object' ? ov : {}
  const form = {}
  for (const k of SCALARS) form[k] = str(o[k])
  form.media = arr(o.media).map((m) => ({
    type: m?.type === 'video' ? 'video' : 'image',
    url: str(m?.url),
    alt: str(m?.alt),
    caption: str(m?.caption),
  }))
  // Homepage showcase tiles (only ever present on the `home` override).
  form.tiles = arr(o.tiles).map(tileFromRaw)
  // Call-to-action buttons — home hero (primary/secondary) + product cta.
  form.primaryCta = ctaFromRaw(o.primaryCta)
  form.secondaryCta = ctaFromRaw(o.secondaryCta)
  form.cta = ctaFromRaw(o.cta)
  // Footer (only the `footer` page): tagline + legal scalars + link columns.
  form.tagline = str(o.tagline)
  form.legal = str(o.legal)
  form.footerColumns = arr(o.columns).map((c) => ({
    header: str(c && c.header),
    links: arr(c && c.links).map((l) => ({ label: str(l && l.label), href: str(l && l.href) })),
  }))
  // Header / navigation (only the `nav` page): centre links + external button.
  form.navLinks = arr(o.links).map((l) => ({ label: str(l && l.label), href: str(l && l.href) }))
  form.navExternal = ctaFromRaw(o.external)
  // Homepage sections (only the `home` page): outcome selector, evidence card, deck lead.
  form.outcomes = arr(o.outcomes).map((x) => ({ label: str(x && x.label), sub: str(x && x.sub), href: str(x && x.href) }))
  const ev = o.evidence && typeof o.evidence === 'object' ? o.evidence : {}
  form.evidence = { label: str(ev.label), heading: str(ev.heading), eyebrow: str(ev.eyebrow), question: str(ev.question), answer: str(ev.answer), caption: str(ev.caption) }
  form.deckLead = str(o.deckLead)
  // Product rich-array fields. Seed from the saved override here; anything the
  // override doesn't carry is filled from live effective content by an effect,
  // so the admin always edits the real current content in place (never blank).
  for (const f of PRODUCT_ARRAY_FIELDS) form[f] = normalizeProductField(f, o[f])
  return form
}

/* Reusable upload button — POSTs the picked file as multipart `file` and hands the
   returned URL back to the caller. Encapsulates its own hidden <input type=file>. */
function UploadButton({ onUploaded, accept, label = 'Upload', small = false }) {
  const ref = useRef(null)
  const [busy, setBusy] = useState(false)

  const onChange = async (e) => {
    const file = e.target.files && e.target.files[0]
    e.target.value = '' // allow re-picking the same file
    if (!file) return
    setBusy(true)
    try {
      const fd = new FormData()
      fd.append('file', file)
      const { data } = await axios.post('/api/v1/admin/marketing/upload', fd)
      const url = data?.url
      if (url) { onUploaded(url); toast.success('Uploaded') }
      else toast.error('Upload succeeded but returned no URL')
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Upload failed')
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <input ref={ref} type="file" accept={accept} className="hidden" onChange={onChange} />
      <button
        type="button"
        onClick={() => ref.current?.click()}
        disabled={busy}
        className={`btn-secondary inline-flex items-center gap-1.5 shrink-0 ${small ? 'text-xs px-2.5 py-1.5' : 'text-sm'} disabled:opacity-50`}
        title="Upload a file and use its URL"
      >
        {busy ? <Loader2 size={small ? 13 : 15} className="animate-spin" /> : <Upload size={small ? 13 : 15} />}
        {busy ? 'Uploading…' : label}
      </button>
    </>
  )
}

/* A labelled text field, optionally with an inline Upload button (for URL fields). */
function Field({ label, hint, value, onChange, textarea, placeholder, onUpload, accept }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-600 mb-1">{label}</label>
      <div className="flex gap-2 items-start">
        {textarea ? (
          <textarea className="input text-sm min-h-[90px] flex-1" placeholder={placeholder}
            value={value} onChange={(e) => onChange(e.target.value)} />
        ) : (
          <input className="input text-sm flex-1" placeholder={placeholder}
            value={value} onChange={(e) => onChange(e.target.value)} />
        )}
        {onUpload && <UploadButton onUploaded={onUpload} accept={accept} />}
      </div>
      {hint && <p className="text-[11px] text-gray-400 mt-1">{hint}</p>}
    </div>
  )
}

/* A repeatable-list card — header (icon + title + Add), blurb, and either an
   empty-state line or the rows. Mirrors the homepage "Showcase tiles" card. */
function ArrayCard({ icon: Icon, title, blurb, count, onAdd, addLabel, empty, children }) {
  return (
    <div className="card">
      <div className="flex items-center gap-2 mb-1">
        <Icon size={16} className="text-primary-600" />
        <h4 className="font-semibold text-gray-900 text-sm">{title}</h4>
        <button onClick={onAdd} className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-primary-600 hover:underline">
          <Plus size={14} /> {addLabel}
        </button>
      </div>
      {blurb && <p className="text-[11px] text-gray-400 mb-3">{blurb}</p>}
      {count === 0 ? (
        <p className="text-sm text-gray-500">{empty}</p>
      ) : (
        <div className="space-y-3">{children}</div>
      )}
    </div>
  )
}

/* One row inside an ArrayCard — the label + move-up/down/remove controls, then
   the row's fields as children. Same control cluster as a Showcase tile. */
function RowShell({ icon: Icon, label, index, total, onUp, onDown, onRemove, children }) {
  return (
    <div className="border border-gray-200 rounded-lg p-3 space-y-3">
      <div className="flex items-center gap-2">
        <span className="text-[11px] text-gray-400 inline-flex items-center gap-1 font-medium">
          {Icon && <Icon size={13} />} {label} {index + 1}
        </span>
        <div className="ml-auto flex items-center gap-0.5">
          <button onClick={onUp} disabled={index === 0}
            className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-700 disabled:opacity-30 disabled:hover:bg-transparent" title="Move up">
            <ArrowUp size={15} />
          </button>
          <button onClick={onDown} disabled={index === total - 1}
            className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-700 disabled:opacity-30 disabled:hover:bg-transparent" title="Move down">
            <ArrowDown size={15} />
          </button>
          <button onClick={onRemove} className="p-1.5 rounded hover:bg-red-50 text-gray-400 hover:text-red-600" title="Remove">
            <Trash2 size={15} />
          </button>
        </div>
      </div>
      {children}
    </div>
  )
}

export default function SiteContent() {
  const [tab, setTab] = useState('content')

  // ── Pages + editor ──
  const [pages, setPages] = useState(null)
  const [err, setErr] = useState(null)
  const [selectedId, setSelectedId] = useState(null)
  const [form, setForm] = useState(null)
  const [originalOverride, setOriginalOverride] = useState({})
  const [saving, setSaving] = useState(false)
  const [clearing, setClearing] = useState(false)

  // ── Draft with AI (Marketing Site Ops agent drafts copy for the selected page) ──
  const [draftGoal, setDraftGoal] = useState('')
  const [drafting, setDrafting] = useState(false)
  const [aiDraft, setAiDraft] = useState(null)

  const draftWithAi = async () => {
    if (!selectedPage || !isRichKind(selectedPage.kind)) return
    setDrafting(true); setAiDraft(null)
    try {
      const { data } = await axios.post('/api/v1/admin/marketing/agent/draft', { id: selectedPage.id, goal: str(draftGoal).trim() })
      const d = data && data.draft && typeof data.draft === 'object' ? data.draft : {}
      if (Object.keys(d).length) { setAiDraft(d); toast.success('Draft ready — review below') }
      else toast(data?.note || 'The agent returned an empty draft. Try a more specific goal.')
    } catch (e) { toast.error(e?.response?.data?.error || 'Draft failed') }
    finally { setDrafting(false) }
  }
  const applyDraft = () => {
    if (!aiDraft) return
    setForm((f) => ({ ...f,
      h1: str(aiDraft.h1) || f.h1,
      intro: str(aiDraft.intro) || f.intro,
      description: str(aiDraft.description) || f.description,
    }))
    toast.success('Applied to the fields — review, then Save changes')
    setAiDraft(null)
  }
  // Reset the AI draft whenever the selected page changes.
  useEffect(() => { setAiDraft(null); setDraftGoal('') }, [selectedId])

  const loadPages = () => {
    setErr(null)
    axios.get('/api/v1/admin/marketing/pages')
      .then((r) => {
        const list = arr(r.data?.pages)
        setPages(list)
        // Keep selection if still present, else select the first page (Homepage).
        setSelectedId((cur) => (list.some((p) => p.id === cur) ? cur : (list[0]?.id ?? null)))
      })
      .catch((e) => {
        setPages([])
        setErr(e?.response?.status === 403 ? 'You are not a platform admin.' : 'Could not load marketing pages.')
      })
  }
  useEffect(loadPages, [])

  const selectedPage = pages?.find((p) => p.id === selectedId) || null

  // When the selected page changes (or pages reload), refill the editor from its override.
  useEffect(() => {
    if (!selectedPage) { setForm(null); setOriginalOverride({}); return }
    const ov = selectedPage.override && typeof selectedPage.override === 'object' ? selectedPage.override : {}
    setOriginalOverride(ov)
    setForm(formFromOverride(ov))
  }, [selectedId, pages]) // eslint-disable-line react-hooks/exhaustive-deps

  // Homepage only: seed the Showcase tiles editor from the CURRENT EFFECTIVE content
  // (GET /api/v1/site/home = built-in defaults merged with any saved override) so the
  // admin edits the live tiles in place instead of from a blank slate. We seed only
  // when there are no saved-override tiles AND the editor is still empty, so a stale
  // async response can never clobber a saved override or unsaved edits.
  useEffect(() => {
    if (selectedId !== 'home') return
    const needTiles = arr(originalOverride.tiles).length === 0
    const needPrimary = !(originalOverride.primaryCta && originalOverride.primaryCta.label)
    const needSecondary = !(originalOverride.secondaryCta && originalOverride.secondaryCta.label)
    const needOutcomes = arr(originalOverride.outcomes).length === 0
    const needEvidence = !(originalOverride.evidence && originalOverride.evidence.question)
    const needDeck = !str(originalOverride.deckLead).trim()
    if (!needTiles && !needPrimary && !needSecondary && !needOutcomes && !needEvidence && !needDeck) return
    let alive = true
    axios.get('/api/v1/site/home')
      .then(({ data }) => {
        if (!alive) return
        const content = data?.content || {}
        const hero = content.hero || {}
        const effTiles = arr(content.tiles).map(tileFromRaw)
        setForm((f) => {
          if (!f) return f
          let changed = false
          const next = { ...f }
          if (needTiles && arr(f.tiles).length === 0 && effTiles.length > 0) { next.tiles = effTiles; changed = true }
          if (needPrimary && !f.primaryCta?.label && hero.primaryCta?.label) { next.primaryCta = ctaFromRaw(hero.primaryCta); changed = true }
          if (needSecondary && !f.secondaryCta?.label && hero.secondaryCta?.label) { next.secondaryCta = ctaFromRaw(hero.secondaryCta); changed = true }
          if (needOutcomes && arr(f.outcomes).length === 0 && arr(content.outcomes).length > 0) {
            next.outcomes = arr(content.outcomes).map((x) => ({ label: str(x && x.label), sub: str(x && x.sub), href: str(x && x.href) })); changed = true
          }
          if (needEvidence && !f.evidence?.question && content.evidence && content.evidence.question) {
            const e = content.evidence
            next.evidence = { label: str(e.label), heading: str(e.heading), eyebrow: str(e.eyebrow), question: str(e.question), answer: str(e.answer), caption: str(e.caption) }; changed = true
          }
          if (needDeck && !str(f.deckLead).trim() && str(content.deckLead).trim()) { next.deckLead = str(content.deckLead); changed = true }
          return changed ? next : f
        })
      })
      .catch(() => { /* defaults still render server-side; leave the editor as-is */ })
    return () => { alive = false }
  }, [selectedId, originalOverride]) // eslint-disable-line react-hooks/exhaustive-deps

  // Product & use-case pages: seed the rich-array editors (whatItIs, howItWorks,
  // faq, …) from the CURRENT EFFECTIVE content (GET /api/v1/site/product|usecase/:slug
  // = built-in defaults merged with any saved override) so the admin edits the live
  // content in place instead of from a blank slate — exactly like the homepage tiles
  // seed. Both content types share the same field shape + `{ product }` envelope. We
  // seed a field only when the saved override doesn't already carry it AND the editor
  // for it is still empty, so a stale async response can't clobber a saved override
  // or unsaved edits.
  useEffect(() => {
    const page = selectedPage
    if (!page || !isRichKind(page.kind) || !page.slug) return
    const needed = PRODUCT_ARRAY_FIELDS.filter((f) => !Array.isArray(originalOverride[f]))
    const needCta = !(originalOverride.cta && originalOverride.cta.label)
    if (needed.length === 0 && !needCta) return // override already seeds every field
    let alive = true
    axios.get(`/api/v1/site/${page.kind === 'usecase' ? 'usecase' : 'product'}/${encodeURIComponent(page.slug)}`)
      .then(({ data }) => {
        if (!alive) return
        const p = data?.product
        if (!p || typeof p !== 'object') return
        setForm((f) => {
          if (!f) return f
          let changed = false
          const next = { ...f }
          for (const field of needed) {
            if (arr(next[field]).length > 0) continue // don't clobber override/edits
            const seed = normalizeProductField(field, p[field])
            if (seed.length > 0) { next[field] = seed; changed = true }
          }
          if (needCta && !f.cta?.label && p.cta?.label) { next.cta = ctaFromRaw(p.cta); changed = true }
          return changed ? next : f
        })
      })
      .catch(() => { /* defaults still render on the live page; leave the editor as-is */ })
    return () => { alive = false }
  }, [selectedId, originalOverride]) // eslint-disable-line react-hooks/exhaustive-deps

  // Footer page: seed the columns/tagline/legal editors from the CURRENT EFFECTIVE
  // footer (GET /api/v1/site/footer = default merged with any override) so the admin
  // edits the live footer in place. Seed only when the override doesn't already
  // carry columns AND the editor is still empty.
  useEffect(() => {
    if (!selectedPage || selectedPage.kind !== 'footer') return
    if (arr(originalOverride.columns).length > 0) return // override already seeds the form
    let alive = true
    axios.get('/api/v1/site/footer')
      .then(({ data }) => {
        if (!alive) return
        const f = data && data.footer && typeof data.footer === 'object' ? data.footer : null
        if (!f) return
        setForm((cur) => {
          if (!cur || arr(cur.footerColumns).length > 0) return cur // don't clobber edits
          return {
            ...cur,
            tagline: cur.tagline || str(f.tagline),
            legal: cur.legal || str(f.legal),
            footerColumns: arr(f.columns).map((c) => ({
              header: str(c && c.header),
              links: arr(c && c.links).map((l) => ({ label: str(l && l.label), href: str(l && l.href) })),
            })),
          }
        })
      })
      .catch(() => { /* default footer still renders; leave the editor as-is */ })
    return () => { alive = false }
  }, [selectedId, originalOverride]) // eslint-disable-line react-hooks/exhaustive-deps

  // Header/navigation page: seed the links + external button from the live nav.
  useEffect(() => {
    if (!selectedPage || selectedPage.kind !== 'nav') return
    if (arr(originalOverride.links).length > 0) return
    let alive = true
    axios.get('/api/v1/site/nav')
      .then(({ data }) => {
        if (!alive) return
        const n = data && data.nav && typeof data.nav === 'object' ? data.nav : null
        if (!n) return
        setForm((cur) => {
          if (!cur || arr(cur.navLinks).length > 0) return cur
          return {
            ...cur,
            navLinks: arr(n.links).map((l) => ({ label: str(l && l.label), href: str(l && l.href) })),
            navExternal: (cur.navExternal && cur.navExternal.label) ? cur.navExternal : ctaFromRaw(n.external),
          }
        })
      })
      .catch(() => { /* default nav still renders; leave the editor as-is */ })
    return () => { alive = false }
  }, [selectedId, originalOverride]) // eslint-disable-line react-hooks/exhaustive-deps

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }))
  const setCtaField = (key, sub, v) => setForm((f) => ({ ...f, [key]: { ...(f[key] && typeof f[key] === 'object' ? f[key] : {}), [sub]: v } }))

  // ── Footer link columns (nested: columns → links) ──
  const setColHeader = (ci, v) => setForm((f) => { const c = arr(f.footerColumns).slice(); c[ci] = { ...c[ci], header: v }; return { ...f, footerColumns: c } })
  const addColumn = () => setForm((f) => ({ ...f, footerColumns: [...arr(f.footerColumns), { header: '', links: [] }] }))
  const removeColumn = (ci) => setForm((f) => ({ ...f, footerColumns: arr(f.footerColumns).filter((_, j) => j !== ci) }))
  const addLink = (ci) => setForm((f) => { const c = arr(f.footerColumns).slice(); c[ci] = { ...c[ci], links: [...arr(c[ci].links), { label: '', href: '' }] }; return { ...f, footerColumns: c } })
  const removeLink = (ci, li) => setForm((f) => { const c = arr(f.footerColumns).slice(); c[ci] = { ...c[ci], links: arr(c[ci].links).filter((_, j) => j !== li) }; return { ...f, footerColumns: c } })
  const setLink = (ci, li, k, v) => setForm((f) => { const c = arr(f.footerColumns).slice(); const ls = arr(c[ci].links).slice(); ls[li] = { ...ls[li], [k]: v }; c[ci] = { ...c[ci], links: ls }; return { ...f, footerColumns: c } })

  // ── Header/nav centre links ──
  const addNavLink = () => setForm((f) => ({ ...f, navLinks: [...arr(f.navLinks), { label: '', href: '' }] }))
  const removeNavLink = (i) => setForm((f) => ({ ...f, navLinks: arr(f.navLinks).filter((_, j) => j !== i) }))
  const setNavLink = (i, k, v) => setForm((f) => { const a = arr(f.navLinks).slice(); a[i] = { ...a[i], [k]: v }; return { ...f, navLinks: a } })

  // ── Homepage outcome cards + evidence card ──
  const addOutcome = () => setForm((f) => ({ ...f, outcomes: [...arr(f.outcomes), { label: '', sub: '', href: '' }] }))
  const removeOutcome = (i) => setForm((f) => ({ ...f, outcomes: arr(f.outcomes).filter((_, j) => j !== i) }))
  const setOutcome = (i, k, v) => setForm((f) => { const a = arr(f.outcomes).slice(); a[i] = { ...a[i], [k]: v }; return { ...f, outcomes: a } })
  const setEvidence = (k, v) => setForm((f) => ({ ...f, evidence: { ...(f.evidence && typeof f.evidence === 'object' ? f.evidence : {}), [k]: v } }))
  const setMediaRow = (i, k, v) => setForm((f) => {
    const media = f.media.slice(); media[i] = { ...media[i], [k]: v }; return { ...f, media }
  })
  const addMediaRow = () => setForm((f) => ({ ...f, media: [...f.media, { type: 'image', url: '', alt: '', caption: '' }] }))
  const removeMediaRow = (i) => setForm((f) => ({ ...f, media: f.media.filter((_, j) => j !== i) }))

  // ── Homepage showcase tiles ──
  const setTile = (i, k, v) => setForm((f) => {
    const tiles = arr(f.tiles).slice(); tiles[i] = { ...tiles[i], [k]: v }; return { ...f, tiles }
  })
  const addTile = () => setForm((f) => ({ ...f, tiles: [...arr(f.tiles), newTile()] }))
  const removeTile = (i) => setForm((f) => ({ ...f, tiles: arr(f.tiles).filter((_, j) => j !== i) }))
  const moveTile = (i, dir) => setForm((f) => {
    const tiles = arr(f.tiles).slice()
    const j = i + dir
    if (j < 0 || j >= tiles.length) return f
    const tmp = tiles[i]; tiles[i] = tiles[j]; tiles[j] = tmp
    return { ...f, tiles }
  })

  // ── Product rich-array fields (generic list helpers) ──
  // `field` is one of PRODUCT_ARRAY_FIELDS. String lists store plain strings;
  // object lists store row objects. setListItem replaces a whole string item;
  // setObjField patches one key of an object row.
  const setListItem = (field, i, v) => setForm((f) => {
    const a = arr(f[field]).slice(); a[i] = v; return { ...f, [field]: a }
  })
  const setObjField = (field, i, k, v) => setForm((f) => {
    const a = arr(f[field]).slice(); a[i] = { ...(a[i] && typeof a[i] === 'object' ? a[i] : {}), [k]: v }; return { ...f, [field]: a }
  })
  const addRow = (field) => setForm((f) => ({ ...f, [field]: [...arr(f[field]), newRow(field)] }))
  const removeRow = (field, i) => setForm((f) => ({ ...f, [field]: arr(f[field]).filter((_, j) => j !== i) }))
  const moveRow = (field, i, dir) => setForm((f) => {
    const a = arr(f[field]).slice()
    const j = i + dir
    if (j < 0 || j >= a.length) return f
    const tmp = a[i]; a[i] = a[j]; a[j] = tmp
    return { ...f, [field]: a }
  })

  const save = async () => {
    if (!selectedPage || !form) return
    setSaving(true)
    try {
      // Preserve any override keys the form does NOT expose (agent-authored copy,
      // faq, cta, …) — setOverride REPLACES the whole object, so send the full state.
      const payload = { ...originalOverride }
      for (const k of SCALARS) payload[k] = form[k]
      payload.media = arr(form.media)
        .map((m) => ({ type: m.type === 'video' ? 'video' : 'image', url: str(m.url).trim(), alt: str(m.alt), caption: str(m.caption) }))
        .filter((m) => m.url) // drop empty rows
      // Homepage showcase tiles — persisted via the PUT `tiles` allowlist. Preserve any
      // unexposed tile fields, trim URLs, keep a stable id, and drop fully-empty tiles.
      if (selectedPage.id === 'home') {
        payload.tiles = arr(form.tiles)
          .map((t, i) => {
            const o = t && typeof t === 'object' ? t : {}
            return {
              ...o,
              id: str(o.id).trim() || `tile-${i + 1}`,
              headline: str(o.headline).trim(),
              tagline: str(o.tagline).trim(),
              ctaLabel: str(o.ctaLabel).trim(),
              ctaHref: str(o.ctaHref).trim(),
              image: str(o.image).trim(),
              video: str(o.video).trim(),
            }
          })
          .filter((t) => t.headline || t.tagline || t.image || t.video || t.ctaLabel)
      }
      // Product / use-case rich-array fields — trim, drop fully-empty rows, preserve
      // any unexposed keys, and let applyArrayField decide inclusion so we never wipe
      // a built-in default with an accidental empty array.
      if (isRichKind(selectedPage.kind)) {
        for (const field of PRODUCT_STRING_LISTS) {
          const cleaned = arr(form[field]).map((s) => str(s).trim()).filter(Boolean)
          applyArrayField(payload, field, cleaned, originalOverride)
        }
        const faqClean = arr(form.faq)
          .map((x) => { const o = x && typeof x === 'object' ? x : {}; return { ...o, q: str(o.q).trim(), a: str(o.a).trim() } })
          .filter((x) => x.q || x.a)
        applyArrayField(payload, 'faq', faqClean, originalOverride)
        const intgClean = arr(form.integrations)
          .map((x) => { const o = x && typeof x === 'object' ? x : {}; return { ...o, name: str(o.name).trim(), note: str(o.note).trim() } })
          .filter((x) => x.name || x.note)
        applyArrayField(payload, 'integrations', intgClean, originalOverride)
        const secClean = arr(form.security)
          .map((x) => { const o = x && typeof x === 'object' ? x : {}; return { ...o, t: str(o.t).trim(), d: str(o.d).trim() } })
          .filter((x) => x.t || x.d)
        applyArrayField(payload, 'security', secClean, originalOverride)
        const insightsClean = arr(form.insights)
          .map((x) => {
            const o = x && typeof x === 'object' ? x : {}
            return {
              ...o,
              q: str(o.q).trim(),
              a: str(o.a).trim(),
              sources: (Array.isArray(o.sources) ? o.sources : str(o.sources).split(','))
                .map((s) => str(s).trim()).filter(Boolean),
              trend: TRENDS.includes(o.trend) ? o.trend : 'flat',
            }
          })
          .filter((x) => x.q || x.a)
        applyArrayField(payload, 'insights', insightsClean, originalOverride)
      }
      // CTA buttons — home hero (primary/secondary) + product/use-case cta. A blank
      // label omits the key so the page falls back to its built-in default CTA.
      const putCta = (key) => {
        const c = form[key] && typeof form[key] === 'object' ? form[key] : {}
        const label = str(c.label).trim(), href = str(c.href).trim()
        if (label) payload[key] = { ...(originalOverride[key] && typeof originalOverride[key] === 'object' ? originalOverride[key] : {}), label, href }
        else delete payload[key]
      }
      if (selectedPage.id === 'home') { putCta('primaryCta'); putCta('secondaryCta') }
      if (isRichKind(selectedPage.kind)) putCta('cta')
      // Homepage sections — outcome cards, evidence card, deck lead.
      if (selectedPage.id === 'home') {
        const outs = arr(form.outcomes)
          .map((o) => ({ label: str(o.label).trim(), sub: str(o.sub).trim(), href: str(o.href).trim() }))
          .filter((o) => o.label)
        if (outs.length) payload.outcomes = outs
        else if (Array.isArray(originalOverride.outcomes)) payload.outcomes = []
        else delete payload.outcomes
        const e = form.evidence && typeof form.evidence === 'object' ? form.evidence : {}
        const evClean = { label: str(e.label).trim(), heading: str(e.heading).trim(), eyebrow: str(e.eyebrow).trim(), question: str(e.question).trim(), answer: str(e.answer).trim(), caption: str(e.caption).trim() }
        if (evClean.question || evClean.answer) payload.evidence = evClean
        else delete payload.evidence
        const dl = str(form.deckLead).trim()
        if (dl) payload.deckLead = dl
        else delete payload.deckLead
      }
      // Footer — tagline, legal, and link columns (drop empty links/columns).
      if (selectedPage.kind === 'footer') {
        payload.tagline = str(form.tagline).trim()
        payload.legal = str(form.legal).trim()
        payload.columns = arr(form.footerColumns)
          .map((c) => ({
            header: str(c.header).trim(),
            links: arr(c.links).map((l) => ({ label: str(l.label).trim(), href: str(l.href).trim() })).filter((l) => l.label),
          }))
          .filter((c) => c.header || c.links.length)
      }
      // Header / navigation — centre links + external button.
      if (selectedPage.kind === 'nav') {
        payload.links = arr(form.navLinks)
          .map((l) => ({ label: str(l.label).trim(), href: str(l.href).trim() }))
          .filter((l) => l.label)
        const ext = form.navExternal && typeof form.navExternal === 'object' ? form.navExternal : {}
        const el = str(ext.label).trim(), eh = str(ext.href).trim()
        if (el) payload.external = { label: el, href: eh }
        else delete payload.external
      }
      const { data } = await axios.put(`/api/v1/admin/marketing/page/${encodeURIComponent(selectedPage.id)}`, payload)
      toast.success('Saved — live on the marketing site')
      // Reflect the server's canonical override back into the list + editor.
      const nextOv = data?.override && typeof data.override === 'object' ? data.override : payload
      setPages((list) => arr(list).map((p) => (p.id === selectedPage.id ? { ...p, override: nextOv } : p)))
      setOriginalOverride(nextOv)
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  const clearOverride = async () => {
    if (!selectedPage) return
    if (!window.confirm(`Clear all overrides for "${selectedPage.label}"? The page reverts to its built-in default.`)) return
    setClearing(true)
    try {
      await axios.delete(`/api/v1/admin/marketing/page/${encodeURIComponent(selectedPage.id)}`)
      toast.success('Override cleared — reverted to default')
      setPages((list) => arr(list).map((p) => (p.id === selectedPage.id ? { ...p, override: {} } : p)))
      setOriginalOverride({})
      setForm(formFromOverride({}))
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Could not clear override')
    } finally {
      setClearing(false)
    }
  }

  // ── Pricing ──
  const [pricing, setPricing] = useState(null)
  const [priceDraft, setPriceDraft] = useState({})
  const [savingPrice, setSavingPrice] = useState(null)

  const loadPricing = () => {
    axios.get('/api/v1/admin/marketing/pricing')
      .then((r) => setPricing(arr(r.data?.pricing)))
      .catch((e) => { setPricing([]); if (e?.response?.status === 403) setErr('You are not a platform admin.') })
  }
  useEffect(() => { if (tab === 'pricing' && pricing === null) loadPricing() }, [tab]) // eslint-disable-line react-hooks/exhaustive-deps

  const savePrice = async (row) => {
    const raw = priceDraft[row.service]
    const n = Number(raw)
    if (!Number.isFinite(n) || n < 0) { toast.error('Enter a valid non-negative price'); return }
    if (!window.confirm(`Publish $${Math.round(n)}/mo for "${row.name}"? This changes what customers are charged.`)) return
    setSavingPrice(row.service)
    try {
      const { data } = await axios.put(`/api/v1/admin/marketing/pricing/${encodeURIComponent(row.service)}`, { price: n })
      const price = Number(data?.price)
      toast.success(`Live price published: $${Number.isFinite(price) ? price : Math.round(n)}/mo`)
      setPricing((list) => arr(list).map((p) => (p.service === row.service ? { ...p, price: Number.isFinite(price) ? price : Math.round(n) } : p)))
      setPriceDraft((d) => { const next = { ...d }; delete next[row.service]; return next })
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Could not publish price')
    } finally {
      setSavingPrice(null)
    }
  }

  // ── Catalog (add / hide / restore products as DATA) ──
  const [catalog, setCatalog] = useState(null)
  const [catHidden, setCatHidden] = useState([])
  const [catClusters, setCatClusters] = useState(['Answer', 'Build', 'Sell', 'Grow', 'Resell'])
  const [newProd, setNewProd] = useState({ slug: '', keyword: '', cluster: 'Answer', price: '' })
  const [catBusy, setCatBusy] = useState(false)

  const loadCatalog = () => {
    axios.get('/api/v1/admin/marketing/catalog')
      .then((r) => {
        setCatalog(arr(r.data?.products))
        setCatHidden(arr(r.data?.hidden))
        if (Array.isArray(r.data?.clusters) && r.data.clusters.length) setCatClusters(r.data.clusters)
      })
      .catch((e) => { setCatalog([]); if (e?.response?.status === 403) setErr('You are not a platform admin.') })
  }
  useEffect(() => { if (tab === 'catalog' && catalog === null) loadCatalog() }, [tab]) // eslint-disable-line react-hooks/exhaustive-deps

  const createProduct = async () => {
    const slug = str(newProd.slug).trim().toLowerCase().replace(/[^a-z0-9-]+/g, '-').replace(/^-+|-+$/g, '')
    const keyword = str(newProd.keyword).trim()
    const price = Number(newProd.price)
    if (!keyword) { toast.error('Enter a product name'); return }
    if (!slug) { toast.error('Enter a URL slug'); return }
    if (!Number.isFinite(price) || price < 0) { toast.error('Enter a price (0 for free)'); return }
    if (!window.confirm(`Create "${keyword}" at ${price === 0 ? 'Free' : '$' + Math.round(price) + '/mo'}? It goes live with its own page, nav entry, and sitemap link.`)) return
    setCatBusy(true)
    try {
      const { data } = await axios.post('/api/v1/admin/marketing/catalog', { slug, keyword, cluster: newProd.cluster, price })
      if (data?.ok) { toast.success(`Created — live at /products/${slug}`); setNewProd({ slug: '', keyword: '', cluster: 'Answer', price: '' }); loadCatalog() }
      else toast.error(data?.error || 'Could not create product')
    } catch (e) { toast.error(e?.response?.data?.error || 'Could not create product') }
    finally { setCatBusy(false) }
  }

  const removeProductRow = async (row) => {
    const isStatic = row.source === 'static'
    if (!window.confirm(`${isStatic ? 'Hide' : 'Delete'} "${str(row.keyword)}"? It disappears from the site, nav, and sitemap.${isStatic ? ' You can restore it later.' : ''}`)) return
    setCatBusy(true)
    try {
      const { data } = await axios.delete(`/api/v1/admin/marketing/catalog/${encodeURIComponent(row.slug)}`)
      if (data?.ok) { toast.success(data.mode === 'hidden' ? 'Hidden' : 'Deleted'); loadCatalog() }
      else toast.error(data?.error || 'Could not remove product')
    } catch (e) { toast.error(e?.response?.data?.error || 'Could not remove product') }
    finally { setCatBusy(false) }
  }

  const restoreProduct = async (slug) => {
    setCatBusy(true)
    try {
      const { data } = await axios.post('/api/v1/admin/marketing/catalog', { slug })
      if (data?.ok) { toast.success('Restored'); loadCatalog() }
      else toast.error(data?.error || 'Could not restore')
    } catch (e) { toast.error(e?.response?.data?.error || 'Could not restore') }
    finally { setCatBusy(false) }
  }

  // ── Site Ops audit ──
  const [audit, setAudit] = useState(null)
  const [auditing, setAuditing] = useState(false)
  const runAudit = async () => {
    setAuditing(true)
    try {
      const { data } = await axios.post('/api/v1/admin/marketing/agent/audit', {})
      // The endpoint may return the audit directly or wrapped under { audit }.
      setAudit(data?.audit && typeof data.audit === 'object' ? data.audit : data)
      toast.success('Audit complete')
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Could not run the Site Ops audit')
    } finally {
      setAuditing(false)
    }
  }

  const overridden = (p) => p?.override && Object.keys(p.override).length > 0

  return (
    <div className="max-w-6xl">
      {/* Header */}
      <div className="flex items-center gap-2 flex-wrap">
        <LayoutTemplate size={20} className="text-primary-600" />
        <h2 className="text-2xl font-bold text-gray-900">Site Content</h2>
        <button onClick={runAudit} disabled={auditing}
          className="ml-auto inline-flex items-center gap-1.5 btn-secondary text-sm disabled:opacity-50">
          {auditing ? <Loader2 size={15} className="animate-spin" /> : <Radar size={15} />}
          {auditing ? 'Auditing…' : 'Run Site Ops audit'}
        </button>
      </div>
      <p className="text-gray-500 mt-1">
        Manage the public <b>ai.reright.io</b> marketing site — copy, images, videos, and pricing for the homepage and every product page. Anything you leave blank falls back to the site's built-in default.
      </p>

      {err && <div className="card mt-6 text-sm text-red-500">{err}</div>}

      {/* Audit results */}
      {audit && (
        <div className="card mt-6">
          <div className="flex items-center gap-2 mb-3">
            <Radar size={16} className="text-primary-600" />
            <h3 className="font-semibold text-gray-900">Site Ops audit</h3>
            {audit.summary && (
              <span className="ml-auto text-xs text-gray-400">
                {str(audit.summary.products)} products · {str(audit.summary.withoutHeroImage)} without hero image · {str(audit.summary.priceDrift)} price drift
              </span>
            )}
          </div>
          {arr(audit.recommendations).length > 0 ? (
            <ul className="space-y-1.5 mb-4">
              {arr(audit.recommendations).map((r, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                  <Sparkles size={14} className="text-primary-600 mt-0.5 shrink-0" /> {str(r)}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-emerald-600 flex items-center gap-1.5 mb-4"><CheckCircle2 size={15} /> No recommendations — the site looks complete.</p>
          )}

          {/* Per-page missing-media flags */}
          {arr(audit.pages).filter((p) => arr(p.missing).length > 0).length > 0 && (
            <div>
              <p className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold mb-2">Pages missing media</p>
              <div className="space-y-1.5">
                {arr(audit.pages).filter((p) => arr(p.missing).length > 0).map((p) => (
                  <div key={p.id} className="flex items-center gap-2 text-sm flex-wrap">
                    <AlertTriangle size={14} className="text-amber-500 shrink-0" />
                    <span className="font-medium text-gray-800">{str(p.name)}</span>
                    {arr(p.missing).map((m, j) => (
                      <span key={j} className="text-[11px] bg-amber-500/10 text-amber-600 rounded-full px-2 py-0.5">{str(m)}</span>
                    ))}
                    {p.priceDrift && <span className="text-[11px] bg-red-500/10 text-red-600 rounded-full px-2 py-0.5">price drift</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
          {audit.note && <p className="text-[11px] text-gray-400 mt-4">{str(audit.note)}</p>}
        </div>
      )}

      {/* Tabs */}
      <div className="flex items-center gap-1 mt-6 border-b border-gray-200">
        {[
          { id: 'content', label: 'Content & media', icon: FileText },
          { id: 'pricing', label: 'Pricing', icon: DollarSign },
          { id: 'catalog', label: 'Catalog', icon: Layers },
        ].map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${
              tab === t.id ? 'border-primary-600 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}>
            <t.icon size={15} /> {t.label}
          </button>
        ))}
      </div>

      {/* ─────────────── CONTENT & MEDIA ─────────────── */}
      {tab === 'content' && !err && (
        <div className="mt-6 flex flex-col md:flex-row gap-6">
          {/* Page list */}
          <div className="md:w-64 shrink-0">
            <p className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold mb-2">Editable pages</p>
            {pages === null ? (
              <div className="text-sm text-gray-500">Loading…</div>
            ) : pages.length === 0 ? (
              <div className="text-sm text-gray-500">No pages.</div>
            ) : (
              <div className="card p-1.5 space-y-0.5">
                {pages.map((p) => (
                  <button key={p.id} onClick={() => setSelectedId(p.id)}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center gap-2 transition-colors ${
                      selectedId === p.id ? 'bg-primary-500/10 text-primary-600 font-semibold' : 'text-gray-700 hover:bg-gray-50'
                    }`}>
                    <span className="truncate flex-1">{str(p.label)}</span>
                    {overridden(p) && (
                      <span className="text-[9px] uppercase tracking-wide bg-emerald-500/10 text-emerald-600 rounded px-1.5 py-0.5 shrink-0">overridden</span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Editor */}
          <div className="flex-1 min-w-0">
            {!selectedPage || !form ? (
              <div className="card text-sm text-gray-500">Select a page to edit.</div>
            ) : (
              <div className="space-y-5">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-lg font-bold text-gray-900">{str(selectedPage.label)}</h3>
                  <span className="text-[11px] text-gray-400 font-mono">{str(selectedPage.id)}</span>
                  {selectedPage.slug && (
                    <a href={`https://ai.reright.io/${selectedPage.kind === 'usecase' ? 'use-cases' : 'products'}/${encodeURIComponent(selectedPage.slug)}`} target="_blank" rel="noopener noreferrer"
                      className="text-xs text-primary-600 hover:underline inline-flex items-center gap-1">
                      View live <ExternalLink size={12} />
                    </a>
                  )}
                  {selectedPage.id === 'home' && (
                    <a href="https://ai.reright.io/home" target="_blank" rel="noopener noreferrer"
                      className="text-xs text-primary-600 hover:underline inline-flex items-center gap-1">
                      View live <ExternalLink size={12} />
                    </a>
                  )}
                </div>

                {selectedPage.id === 'home' && (
                  <p className="text-[12px] text-gray-600 rounded-lg bg-primary-500/5 border border-primary-500/15 px-3 py-2 leading-relaxed">
                    <b>Hero</b> = the top image/video · <b>Showcase tiles</b> = the big scrolling sections · <b>Media/gallery</b> = the demo video + extra images.
                  </p>
                )}

                {selectedPage.kind !== 'footer' && selectedPage.kind !== 'nav' && (
                <div className="card space-y-4">
                  <p className="text-[11px] text-gray-400 -mt-1">
                    Every field is optional. Leave one blank to fall back to the page's built-in default.
                  </p>
                  <Field label="Title (meta / browser tab)" value={form.title} onChange={(v) => setField('title', v)}
                    placeholder="e.g. AI Support Chatbot | ReRight" hint="Used for <title> and social/OG title." />
                  <Field label="Meta description" textarea value={form.description} onChange={(v) => setField('description', v)}
                    placeholder="One or two sentences for search + social previews." />
                  <Field label="H1 (page headline)" value={form.h1} onChange={(v) => setField('h1', v)}
                    placeholder="The big headline shown on the page." />
                  <Field label="Intro" textarea value={form.intro} onChange={(v) => setField('intro', v)}
                    placeholder="The opening paragraph under the headline." />
                  <Field label="Hero image URL" value={form.heroImage} onChange={(v) => setField('heroImage', v)}
                    placeholder="https://…/hero.png" onUpload={(url) => setField('heroImage', url)} accept="image/*"
                    hint="Shown as the page's hero and og:image." />
                  <Field label="Hero video URL" value={form.heroVideo} onChange={(v) => setField('heroVideo', v)}
                    placeholder="https://…/demo.mp4" onUpload={(url) => setField('heroVideo', url)} accept="video/*"
                    hint="Optional demo loop shown in the hero." />
                  <Field label="Hero alt text" value={form.heroAlt} onChange={(v) => setField('heroAlt', v)}
                    placeholder="Accessible description of the hero image." />
                </div>
                )}

                {/* Footer editor — the public marketing footer (tagline + link columns + legal). */}
                {selectedPage.kind === 'footer' && (
                  <>
                    <div className="card space-y-4">
                      <Field label="Brand tagline" textarea value={str(form.tagline)} onChange={(v) => setField('tagline', v)}
                        placeholder="The short line under the logo in the footer." />
                      <Field label="Legal / copyright line" value={str(form.legal)} onChange={(v) => setField('legal', v)}
                        placeholder="© 2026 ReRight · …" />
                    </div>
                    <div className="card">
                      <div className="flex items-center gap-2 mb-1">
                        <Layers size={16} className="text-primary-600" />
                        <h4 className="font-semibold text-gray-900 text-sm">Link columns</h4>
                        <button onClick={addColumn} className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-primary-600 hover:underline">
                          <Plus size={14} /> Add column
                        </button>
                      </div>
                      <p className="text-[11px] text-gray-400 mb-3">Each column has a heading and a list of links. Use a path (<code className="text-[10px] bg-gray-100 px-1 rounded">/products</code>) or a full URL (<code className="text-[10px] bg-gray-100 px-1 rounded">https://…</code>).</p>
                      {arr(form.footerColumns).length === 0 ? (
                        <p className="text-sm text-gray-500">No columns yet — add one, or they’ll load from the live footer in a moment.</p>
                      ) : (
                        <div className="space-y-4">
                          {form.footerColumns.map((col, ci) => (
                            <div key={ci} className="border border-gray-200 rounded-lg p-3 space-y-3">
                              <div className="flex items-center gap-2">
                                <input className="input text-sm font-semibold flex-1" placeholder="Column heading — e.g. Product"
                                  value={str(col.header)} onChange={(e) => setColHeader(ci, e.target.value)} />
                                <button onClick={() => addLink(ci)} className="inline-flex items-center gap-1 text-xs font-semibold text-primary-600 hover:underline shrink-0">
                                  <Plus size={13} /> Link
                                </button>
                                <button onClick={() => removeColumn(ci)} className="p-1.5 rounded hover:bg-red-50 text-gray-400 hover:text-red-600 shrink-0" title="Remove column">
                                  <Trash2 size={15} />
                                </button>
                              </div>
                              {arr(col.links).length === 0 ? (
                                <p className="text-[12px] text-gray-400">No links yet — add one.</p>
                              ) : (
                                <div className="space-y-2">
                                  {col.links.map((lnk, li) => (
                                    <div key={li} className="flex gap-2 items-center">
                                      <input className="input text-sm flex-1" placeholder="Label"
                                        value={str(lnk.label)} onChange={(e) => setLink(ci, li, 'label', e.target.value)} />
                                      <input className="input text-sm flex-1" placeholder="/path or https://…"
                                        value={str(lnk.href)} onChange={(e) => setLink(ci, li, 'href', e.target.value)} />
                                      <button onClick={() => removeLink(ci, li)} className="p-1.5 rounded hover:bg-red-50 text-gray-400 hover:text-red-600 shrink-0" title="Remove link">
                                        <Trash2 size={14} />
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </>
                )}

                {/* Header / navigation editor — centre links + external button.
                    The Products mega-menu is catalog-driven and not edited here. */}
                {selectedPage.kind === 'nav' && (
                  <>
                    <div className="card">
                      <div className="flex items-center gap-2 mb-1">
                        <LayoutTemplate size={16} className="text-primary-600" />
                        <h4 className="font-semibold text-gray-900 text-sm">Navigation links</h4>
                        <button onClick={addNavLink} className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-primary-600 hover:underline">
                          <Plus size={14} /> Add link
                        </button>
                      </div>
                      <p className="text-[11px] text-gray-400 mb-3">The centre links in the top bar. (The <b>Products</b> menu is built from the catalog automatically.) Use a path (<code className="text-[10px] bg-gray-100 px-1 rounded">/use-cases</code>) or a full URL.</p>
                      {arr(form.navLinks).length === 0 ? (
                        <p className="text-sm text-gray-500">No links yet — add one, or they’ll load from the live nav in a moment.</p>
                      ) : (
                        <div className="space-y-2">
                          {form.navLinks.map((l, i) => (
                            <div key={i} className="flex gap-2 items-center">
                              <input className="input text-sm flex-1" placeholder="Label" value={str(l.label)} onChange={(e) => setNavLink(i, 'label', e.target.value)} />
                              <input className="input text-sm flex-1" placeholder="/path or https://…" value={str(l.href)} onChange={(e) => setNavLink(i, 'href', e.target.value)} />
                              <button onClick={() => removeNavLink(i)} className="p-1.5 rounded hover:bg-red-50 text-gray-400 hover:text-red-600 shrink-0" title="Remove link"><Trash2 size={14} /></button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="card space-y-3">
                      <div className="flex items-center gap-2">
                        <ExternalLink size={16} className="text-primary-600" />
                        <h4 className="font-semibold text-gray-900 text-sm">External button</h4>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <Field label="Label" value={str(form.navExternal?.label)} onChange={(v) => setCtaField('navExternal', 'label', v)} placeholder="e.g. reright.io" />
                        <Field label="Link" value={str(form.navExternal?.href)} onChange={(v) => setCtaField('navExternal', 'href', v)} placeholder="https://reright.io" />
                      </div>
                      <p className="text-[11px] text-gray-400">The outlined button on the right of the nav (shown with a ↗). Leave the label blank to fall back to the default.</p>
                    </div>
                  </>
                )}

                {/* Call-to-action buttons — home hero (primary + secondary) or a
                    single product/use-case cta. Blank label → built-in default. */}
                {(selectedPage.id === 'home' || isRichKind(selectedPage.kind)) && (
                  <div className="card space-y-4">
                    <div className="flex items-center gap-2">
                      <ExternalLink size={16} className="text-primary-600" />
                      <h4 className="font-semibold text-gray-900 text-sm">Call to action</h4>
                    </div>
                    {selectedPage.id === 'home' ? (
                      <>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <Field label="Primary button label" value={str(form.primaryCta?.label)} onChange={(v) => setCtaField('primaryCta', 'label', v)} placeholder="e.g. Explore AI services" />
                          <Field label="Primary button link" value={str(form.primaryCta?.href)} onChange={(v) => setCtaField('primaryCta', 'href', v)} placeholder="#services · /products · https://…" />
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <Field label="Secondary button label" value={str(form.secondaryCta?.label)} onChange={(v) => setCtaField('secondaryCta', 'label', v)} placeholder="e.g. See how it works" />
                          <Field label="Secondary button link" value={str(form.secondaryCta?.href)} onChange={(v) => setCtaField('secondaryCta', 'href', v)} placeholder="#useful · /guide" />
                        </div>
                      </>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <Field label="Button label" value={str(form.cta?.label)} onChange={(v) => setCtaField('cta', 'label', v)} placeholder="e.g. Start with AI Support Chatbot →" />
                        <Field label="Button link" value={str(form.cta?.href)} onChange={(v) => setCtaField('cta', 'href', v)} placeholder="/signup" />
                      </div>
                    )}
                    <p className="text-[11px] text-gray-400">Leave a label blank to fall back to the page's built-in default CTA.</p>
                  </div>
                )}

                {/* Showcase tiles — homepage only. These are the big full-bleed sections
                    that scroll down the home page; each carries its own <MediaSlot>. */}
                {selectedPage.id === 'home' && (
                  <div className="card">
                    <div className="flex items-center gap-2 mb-1">
                      <LayoutTemplate size={16} className="text-primary-600" />
                      <h4 className="font-semibold text-gray-900 text-sm">Showcase tiles</h4>
                      <button onClick={addTile} className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-primary-600 hover:underline">
                        <Plus size={14} /> Add tile
                      </button>
                    </div>
                    <p className="text-[11px] text-gray-400 mb-3">
                      The big full-bleed sections that scroll down the homepage. Each has its own headline, copy, call-to-action, and an image or video slot. Leave a slot blank to show a labelled placeholder frame.
                    </p>
                    {arr(form.tiles).length === 0 ? (
                      <p className="text-sm text-gray-500">No tiles yet — add one, or they'll load from the live homepage in a moment.</p>
                    ) : (
                      <div className="space-y-4">
                        {form.tiles.map((t, i) => (
                          <div key={t.id || i} className="border border-gray-200 rounded-lg p-3 space-y-3">
                            <div className="flex items-center gap-2">
                              <span className="text-[11px] text-gray-400 inline-flex items-center gap-1 font-medium">
                                <LayoutTemplate size={13} /> Tile {i + 1}
                              </span>
                              <div className="ml-auto flex items-center gap-0.5">
                                <button onClick={() => moveTile(i, -1)} disabled={i === 0}
                                  className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-700 disabled:opacity-30 disabled:hover:bg-transparent" title="Move up">
                                  <ArrowUp size={15} />
                                </button>
                                <button onClick={() => moveTile(i, 1)} disabled={i === form.tiles.length - 1}
                                  className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-700 disabled:opacity-30 disabled:hover:bg-transparent" title="Move down">
                                  <ArrowDown size={15} />
                                </button>
                                <button onClick={() => removeTile(i)} className="p-1.5 rounded hover:bg-red-50 text-gray-400 hover:text-red-600" title="Remove tile">
                                  <Trash2 size={15} />
                                </button>
                              </div>
                            </div>
                            <Field label="Headline" value={t.headline} onChange={(v) => setTile(i, 'headline', v)}
                              placeholder="The big headline for this section." />
                            <Field label="Tagline" textarea value={t.tagline} onChange={(v) => setTile(i, 'tagline', v)}
                              placeholder="One or two supporting sentences under the headline." />
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              <Field label="CTA label" value={t.ctaLabel} onChange={(v) => setTile(i, 'ctaLabel', v)}
                                placeholder="e.g. Explore the chatbot" />
                              <Field label="CTA link" value={t.ctaHref} onChange={(v) => setTile(i, 'ctaHref', v)}
                                placeholder="/products/answers or #services" />
                            </div>
                            <Field label="Image URL" value={t.image} onChange={(v) => setTile(i, 'image', v)}
                              placeholder="https://…/tile.png" onUpload={(url) => setTile(i, 'image', url)} accept="image/*"
                              hint="Shown as this section's media when no video is set." />
                            <Field label="Video URL" value={t.video} onChange={(v) => setTile(i, 'video', v)}
                              placeholder="https://…/demo.mp4" onUpload={(url) => setTile(i, 'video', url)} accept="video/*"
                              hint="Optional autoplay loop — takes priority over the image." />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Homepage sections — outcome selector, evidence card, deck lead. */}
                {selectedPage.id === 'home' && (
                  <>
                    <div className="card">
                      <Field label="Deck section lead" textarea value={str(form.deckLead)} onChange={(v) => setField('deckLead', v)}
                        placeholder="The sentence under “Switch one on — and watch it work.”" />
                    </div>

                    <div className="card">
                      <div className="flex items-center gap-2 mb-1">
                        <ListChecks size={16} className="text-primary-600" />
                        <h4 className="font-semibold text-gray-900 text-sm">Outcome cards</h4>
                        <button onClick={addOutcome} className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-primary-600 hover:underline">
                          <Plus size={14} /> Add outcome
                        </button>
                      </div>
                      <p className="text-[11px] text-gray-400 mb-3">The “Start with your outcome” cards. Each has a label, a sub-line, and a link (a path like <code className="text-[10px] bg-gray-100 px-1 rounded">/products/…</code> or a <code className="text-[10px] bg-gray-100 px-1 rounded">#section</code> anchor).</p>
                      {arr(form.outcomes).length === 0 ? (
                        <p className="text-sm text-gray-500">No outcome cards yet — add one, or they’ll load from the live homepage in a moment.</p>
                      ) : (
                        <div className="space-y-3">
                          {form.outcomes.map((o, i) => (
                            <RowShell key={i} icon={ListChecks} label="Outcome" index={i} total={arr(form.outcomes).length}
                              onUp={() => setForm((f) => { const a = arr(f.outcomes).slice(); if (i === 0) return f; const t = a[i - 1]; a[i - 1] = a[i]; a[i] = t; return { ...f, outcomes: a } })}
                              onDown={() => setForm((f) => { const a = arr(f.outcomes).slice(); if (i === a.length - 1) return f; const t = a[i + 1]; a[i + 1] = a[i]; a[i] = t; return { ...f, outcomes: a } })}
                              onRemove={() => removeOutcome(i)}>
                              <Field label="Label" value={str(o.label)} onChange={(v) => setOutcome(i, 'label', v)} placeholder="e.g. Answer customers" />
                              <Field label="Sub-line" value={str(o.sub)} onChange={(v) => setOutcome(i, 'sub', v)} placeholder="e.g. 24/7 support, grounded in your content" />
                              <Field label="Link" value={str(o.href)} onChange={(v) => setOutcome(i, 'href', v)} placeholder="/products/… or #services" />
                            </RowShell>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="card space-y-3">
                      <div className="flex items-center gap-2">
                        <Sparkles size={16} className="text-primary-600" />
                        <h4 className="font-semibold text-gray-900 text-sm">Evidence card</h4>
                      </div>
                      <p className="text-[11px] text-gray-400">The single “An answer you can trace” example on the homepage. Keep it clearly illustrative — not a real customer result.</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <Field label="Section label" value={str(form.evidence?.label)} onChange={(v) => setEvidence('label', v)} placeholder="e.g. Marketing intelligence" />
                        <Field label="Heading" value={str(form.evidence?.heading)} onChange={(v) => setEvidence('heading', v)} placeholder="e.g. An answer you can trace." />
                      </div>
                      <Field label="Question" value={str(form.evidence?.question)} onChange={(v) => setEvidence('question', v)} placeholder="The example question." />
                      <Field label="Answer" textarea value={str(form.evidence?.answer)} onChange={(v) => setEvidence('answer', v)} placeholder="The source-linked answer." />
                      <Field label="Caption" value={str(form.evidence?.caption)} onChange={(v) => setEvidence('caption', v)} placeholder="e.g. Illustrative · source-linked answer from ads, analytics & CRM." />
                    </div>
                  </>
                )}

                {/* Product / use-case rich-array content. Seeded from the live
                    effective page so the admin edits the real current copy in place.
                    Order matches the live page: what it is → how it works → use cases
                    → does/doesn't → faq → integrations → security. */}
                {isRichKind(selectedPage.kind) && (
                  <>
                    {/* Draft with AI — the Marketing Site Ops agent drafts copy from the page's facts. */}
                    <div className="card space-y-3" style={{ borderColor: 'rgba(229,55,43,0.28)' }}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Sparkles size={16} className="text-primary-600" />
                        <h4 className="font-semibold text-gray-900 text-sm">Draft with AI</h4>
                        <span className="text-[10px] uppercase tracking-wide bg-primary-500/10 text-primary-600 rounded px-1.5 py-0.5">Marketing Site Ops agent</span>
                      </div>
                      <p className="text-[11px] text-gray-400">Tell the agent what you want — it drafts a tighter headline, intro, and meta from this page's real facts. Grounded, never auto-published: review, then apply.</p>
                      <div className="flex gap-2 items-start">
                        <input className="input text-sm flex-1" placeholder="e.g. sharper and benefit-led, aimed at marketing agencies"
                          value={draftGoal} onChange={(e) => setDraftGoal(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); draftWithAi() } }} />
                        <button onClick={draftWithAi} disabled={drafting} className="btn-primary inline-flex items-center gap-1.5 shrink-0 disabled:opacity-50">
                          {drafting ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />}
                          {drafting ? 'Drafting…' : 'Draft with AI'}
                        </button>
                      </div>
                      {aiDraft && (
                        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-2.5 text-sm">
                          {aiDraft.h1 && <div><span className="text-[10px] font-bold text-gray-400 uppercase tracking-wide">Headline (H1)</span><p className="text-gray-800 mt-0.5">{str(aiDraft.h1)}</p></div>}
                          {aiDraft.intro && <div><span className="text-[10px] font-bold text-gray-400 uppercase tracking-wide">Intro</span><p className="text-gray-800 mt-0.5">{str(aiDraft.intro)}</p></div>}
                          {aiDraft.description && <div><span className="text-[10px] font-bold text-gray-400 uppercase tracking-wide">Meta description</span><p className="text-gray-800 mt-0.5">{str(aiDraft.description)}</p></div>}
                          <div className="flex items-center gap-2 pt-1 flex-wrap">
                            <button onClick={applyDraft} className="btn-primary text-xs px-3 py-1.5 inline-flex items-center gap-1.5"><Check size={13} /> Apply to fields</button>
                            <button onClick={() => setAiDraft(null)} className="btn-secondary text-xs px-3 py-1.5">Discard</button>
                            <span className="text-[11px] text-gray-400 ml-auto">Applying fills the fields above — you still Save.</span>
                          </div>
                        </div>
                      )}
                    </div>

                    <p className="text-[12px] text-gray-600 rounded-lg bg-primary-500/5 border border-primary-500/15 px-3 py-2 leading-relaxed">
                      These sections are the body of the {selectedPage.kind === 'usecase' ? 'use-case' : 'product'} page. They start from the live content, so you’re editing what’s published now. Remove every row in a section to hide it; clear the whole page with <b>Clear override</b> below.
                    </p>

                    {/* Plain-text list sections */}
                    {PRODUCT_STRING_LISTS.map((field) => {
                      const meta = STRING_LIST_META[field]
                      const items = arr(form[field])
                      return (
                        <ArrayCard key={field} icon={meta.icon} title={meta.title} blurb={meta.blurb}
                          count={items.length} onAdd={() => addRow(field)} addLabel={meta.add} empty={meta.empty}>
                          {items.map((val, i) => (
                            <RowShell key={i} icon={meta.icon} label={meta.item} index={i} total={items.length}
                              onUp={() => moveRow(field, i, -1)} onDown={() => moveRow(field, i, 1)} onRemove={() => removeRow(field, i)}>
                              <textarea className="input text-sm min-h-[70px] w-full" placeholder={meta.placeholder}
                                value={str(val)} onChange={(e) => setListItem(field, i, e.target.value)} />
                            </RowShell>
                          ))}
                        </ArrayCard>
                      )
                    })}

                    {/* FAQ — {q, a} */}
                    <ArrayCard icon={HelpCircle} title="FAQ" blurb="The accordion at the bottom of the page. A question and its answer per row."
                      count={arr(form.faq).length} onAdd={() => addRow('faq')} addLabel="Add question"
                      empty="No FAQs yet — add one, or they’ll load from the live page in a moment.">
                      {arr(form.faq).map((row, i) => (
                        <RowShell key={i} icon={HelpCircle} label="Q" index={i} total={arr(form.faq).length}
                          onUp={() => moveRow('faq', i, -1)} onDown={() => moveRow('faq', i, 1)} onRemove={() => removeRow('faq', i)}>
                          <Field label="Question" value={str(row.q)} onChange={(v) => setObjField('faq', i, 'q', v)}
                            placeholder="e.g. How long does setup take?" />
                          <Field label="Answer" textarea value={str(row.a)} onChange={(v) => setObjField('faq', i, 'a', v)}
                            placeholder="A clear, honest answer." />
                        </RowShell>
                      ))}
                    </ArrayCard>

                    {/* Integrations — {name, note} */}
                    <ArrayCard icon={Plug} title="Data sources / integrations" blurb="The cards under “Connects to the tools you already use”. A name, with an optional note."
                      count={arr(form.integrations).length} onAdd={() => addRow('integrations')} addLabel="Add integration"
                      empty="No integrations yet — add one, or they’ll load from the live page in a moment.">
                      {arr(form.integrations).map((row, i) => (
                        <RowShell key={i} icon={Plug} label="Source" index={i} total={arr(form.integrations).length}
                          onUp={() => moveRow('integrations', i, -1)} onDown={() => moveRow('integrations', i, 1)} onRemove={() => removeRow('integrations', i)}>
                          <Field label="Name" value={str(row.name)} onChange={(v) => setObjField('integrations', i, 'name', v)}
                            placeholder="e.g. Zendesk" />
                          <Field label="Note (optional)" value={str(row.note)} onChange={(v) => setObjField('integrations', i, 'note', v)}
                            placeholder="A short line on how it connects." />
                        </RowShell>
                      ))}
                    </ArrayCard>

                    {/* Security — {t, d} */}
                    <ArrayCard icon={Shield} title="Security & control" blurb="The points under “Your data, your approval”. A short title and a description per row."
                      count={arr(form.security).length} onAdd={() => addRow('security')} addLabel="Add point"
                      empty="No security points yet — add one, or they’ll load from the live page in a moment.">
                      {arr(form.security).map((row, i) => (
                        <RowShell key={i} icon={Shield} label="Point" index={i} total={arr(form.security).length}
                          onUp={() => moveRow('security', i, -1)} onDown={() => moveRow('security', i, 1)} onRemove={() => removeRow('security', i)}>
                          <Field label="Title" value={str(row.t)} onChange={(v) => setObjField('security', i, 't', v)}
                            placeholder="e.g. You approve every action" />
                          <Field label="Description" textarea value={str(row.d)} onChange={(v) => setObjField('security', i, 'd', v)}
                            placeholder="What this control means in practice." />
                        </RowShell>
                      ))}
                    </ArrayCard>

                    {/* Insight cards — the "kind of answers you get" carousel {q,a,sources,trend}.
                        Product pages only (that's where <ProductPage> renders the carousel). */}
                    {selectedPage.kind === 'product' && (
                      <ArrayCard icon={Sparkles} title="Insight cards (answers carousel)"
                        blurb="The scrollable “The kind of answers you get” cards below the hero. Each is an illustrative example — a question, the answer, the sources behind it, and a trend arrow."
                        count={arr(form.insights).length} onAdd={() => addRow('insights')} addLabel="Add insight"
                        empty="No insight cards yet — add one to build the answers carousel.">
                        {arr(form.insights).map((row, i) => (
                          <RowShell key={i} icon={Sparkles} label="Insight" index={i} total={arr(form.insights).length}
                            onUp={() => moveRow('insights', i, -1)} onDown={() => moveRow('insights', i, 1)} onRemove={() => removeRow('insights', i)}>
                            <Field label="Question" value={str(row.q)} onChange={(v) => setObjField('insights', i, 'q', v)}
                              placeholder="e.g. Why did qualified leads drop last week?" />
                            <Field label="Answer" textarea value={str(row.a)} onChange={(v) => setObjField('insights', i, 'a', v)}
                              placeholder="The source-linked answer this product would give." />
                            <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 items-start">
                              <Field label="Sources (comma-separated)"
                                value={Array.isArray(row.sources) ? row.sources.join(', ') : str(row.sources)}
                                onChange={(v) => setObjField('insights', i, 'sources', v)}
                                placeholder="e.g. Meta Ads, GA4" hint="Shown as source pills on the card." />
                              <div>
                                <label className="block text-xs font-semibold text-gray-600 mb-1">Trend</label>
                                <select className="input text-sm" value={TRENDS.includes(row.trend) ? row.trend : 'flat'}
                                  onChange={(e) => setObjField('insights', i, 'trend', e.target.value)}>
                                  <option value="up">Up ↑</option>
                                  <option value="down">Down ↓</option>
                                  <option value="flat">Flat –</option>
                                </select>
                              </div>
                            </div>
                          </RowShell>
                        ))}
                      </ArrayCard>
                    )}
                  </>
                )}

                {/* Media gallery */}
                {selectedPage.kind !== 'footer' && selectedPage.kind !== 'nav' && (
                <div className="card">
                  <div className="flex items-center gap-2 mb-3">
                    <ImageIcon size={16} className="text-primary-600" />
                    <h4 className="font-semibold text-gray-900 text-sm">Media gallery</h4>
                    <button onClick={addMediaRow} className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-primary-600 hover:underline">
                      <Plus size={14} /> Add media
                    </button>
                  </div>
                  {form.media.length === 0 ? (
                    <p className="text-sm text-gray-500">No media yet. Add an image or video to show in the page gallery.</p>
                  ) : (
                    <div className="space-y-3">
                      {form.media.map((m, i) => (
                        <div key={i} className="border border-gray-200 rounded-lg p-3 space-y-2">
                          <div className="flex items-center gap-2 flex-wrap">
                            <select className="input text-sm w-auto" value={m.type} onChange={(e) => setMediaRow(i, 'type', e.target.value)}>
                              <option value="image">Image</option>
                              <option value="video">Video</option>
                            </select>
                            <span className="text-[11px] text-gray-400 inline-flex items-center gap-1">
                              {m.type === 'video' ? <Video size={13} /> : <ImageIcon size={13} />} row {i + 1}
                            </span>
                            <button onClick={() => removeMediaRow(i)} className="ml-auto p-1.5 rounded hover:bg-red-50 text-gray-400 hover:text-red-600" title="Remove">
                              <Trash2 size={15} />
                            </button>
                          </div>
                          <div className="flex gap-2 items-start">
                            <input className="input text-sm flex-1" placeholder="Media URL — https://…"
                              value={m.url} onChange={(e) => setMediaRow(i, 'url', e.target.value)} />
                            <UploadButton small accept={m.type === 'video' ? 'video/*' : 'image/*'} onUploaded={(url) => setMediaRow(i, 'url', url)} />
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <input className="input text-sm" placeholder="Alt text"
                              value={m.alt} onChange={(e) => setMediaRow(i, 'alt', e.target.value)} />
                            <input className="input text-sm" placeholder="Caption"
                              value={m.caption} onChange={(e) => setMediaRow(i, 'caption', e.target.value)} />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                )}

                {/* Actions */}
                <div className="flex items-center gap-3 flex-wrap">
                  <button onClick={save} disabled={saving} className="btn-primary inline-flex items-center gap-1.5 disabled:opacity-50">
                    {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                    {saving ? 'Saving…' : 'Save changes'}
                  </button>
                  <button onClick={clearOverride} disabled={clearing || !overridden(selectedPage)}
                    className="btn-secondary inline-flex items-center gap-1.5 disabled:opacity-50"
                    title={overridden(selectedPage) ? 'Revert this page to its built-in default' : 'No override to clear'}>
                    {clearing ? <Loader2 size={16} className="animate-spin" /> : <RotateCcw size={16} />}
                    Clear override
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ─────────────── PRICING ─────────────── */}
      {tab === 'pricing' && !err && (
        <div className="mt-6">
          <div className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 mb-5">
            <AlertTriangle size={16} className="text-amber-600 mt-0.5 shrink-0" />
            <p className="text-sm text-amber-700">
              <b>Publishing a live price is a deliberate action</b> — it changes what customers are charged. Catalog default is the fallback; publishing overrides it via <code className="text-xs bg-amber-500/10 px-1 rounded">PRICE_&lt;SERVICE&gt;</code>.
            </p>
          </div>

          {pricing === null ? (
            <div className="card text-sm text-gray-500">Loading pricing…</div>
          ) : pricing.length === 0 ? (
            <div className="card text-sm text-gray-500">No services.</div>
          ) : (
            <div className="card p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wide text-gray-500 border-b border-gray-200">
                    <th className="px-4 py-3 font-semibold">Service</th>
                    <th className="px-4 py-3 font-semibold">Current live price</th>
                    <th className="px-4 py-3 font-semibold">Catalog default</th>
                    <th className="px-4 py-3 font-semibold">New price</th>
                    <th className="px-4 py-3 font-semibold"></th>
                  </tr>
                </thead>
                <tbody>
                  {pricing.map((row) => {
                    const drift = Number(row.price) !== Number(row.catalogDefault)
                    return (
                      <tr key={row.service} className="border-b border-gray-200/60">
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900">{str(row.name)}</div>
                          <div className="text-[11px] text-gray-400 font-mono">{str(row.service)}</div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="font-semibold text-gray-900">${str(row.price)}</span><span className="text-gray-400">/mo</span>
                          {drift && <span className="ml-2 text-[10px] uppercase tracking-wide bg-amber-500/10 text-amber-600 rounded px-1.5 py-0.5">overridden</span>}
                        </td>
                        <td className="px-4 py-3 text-gray-500">${str(row.catalogDefault)}/mo</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            <span className="text-gray-400">$</span>
                            <input type="number" min="0" step="1"
                              className="input text-sm w-24"
                              placeholder={str(row.price)}
                              value={priceDraft[row.service] ?? ''}
                              onChange={(e) => setPriceDraft((d) => ({ ...d, [row.service]: e.target.value }))} />
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <button onClick={() => savePrice(row)}
                            disabled={savingPrice === row.service || priceDraft[row.service] == null || priceDraft[row.service] === ''}
                            className="btn-primary text-sm px-4 py-1.5 disabled:opacity-50">
                            {savingPrice === row.service ? 'Publishing…' : 'Publish'}
                          </button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
          <p className="text-xs text-gray-400 mt-3">Prices are whole dollars per month. The catalog default is the single source of truth; a published price overrides it live.</p>
        </div>
      )}

      {/* ─────────────── CATALOG ─────────────── */}
      {tab === 'catalog' && !err && (
        <div className="mt-6 space-y-6">
          {/* Add a product */}
          <div className="card">
            <div className="flex items-center gap-2 mb-2">
              <Plus size={16} className="text-primary-600" />
              <h3 className="font-semibold text-gray-900">Add a product</h3>
            </div>
            <p className="text-[12px] text-gray-500 mb-4">
              A new product goes live immediately with its own <code className="text-[11px] bg-gray-100 px-1 rounded">/products/&lt;slug&gt;</code> page, a mega-nav entry, and a sitemap link. Add its copy afterwards on the <b>Content &amp; media</b> tab.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Product name</label>
                <input className="input text-sm" placeholder="e.g. Voice AI Receptionist" value={newProd.keyword}
                  onChange={(e) => setNewProd((p) => ({ ...p, keyword: e.target.value }))} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">URL slug</label>
                <input className="input text-sm" placeholder="voice-ai-receptionist" value={newProd.slug}
                  onChange={(e) => setNewProd((p) => ({ ...p, slug: e.target.value }))} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Lane</label>
                <select className="input text-sm" value={newProd.cluster} onChange={(e) => setNewProd((p) => ({ ...p, cluster: e.target.value }))}>
                  {catClusters.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Price ($/mo · 0 = free)</label>
                <input type="number" min="0" step="1" className="input text-sm" placeholder="89" value={newProd.price}
                  onChange={(e) => setNewProd((p) => ({ ...p, price: e.target.value }))} />
              </div>
            </div>
            <button onClick={createProduct} disabled={catBusy} className="btn-primary inline-flex items-center gap-1.5 mt-4 disabled:opacity-50">
              {catBusy ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Create product
            </button>
          </div>

          {/* Live products */}
          {catalog === null ? (
            <div className="card text-sm text-gray-500">Loading catalog…</div>
          ) : catalog.length === 0 ? (
            <div className="card text-sm text-gray-500">No products.</div>
          ) : (
            <div className="card p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wide text-gray-500 border-b border-gray-200">
                    <th className="px-4 py-3 font-semibold">Product</th>
                    <th className="px-4 py-3 font-semibold">Lane</th>
                    <th className="px-4 py-3 font-semibold">Price</th>
                    <th className="px-4 py-3 font-semibold">Source</th>
                    <th className="px-4 py-3 font-semibold"></th>
                  </tr>
                </thead>
                <tbody>
                  {catalog.map((row) => (
                    <tr key={row.slug} className="border-b border-gray-200/60">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{str(row.keyword)}</div>
                        <div className="text-[11px] text-gray-400 font-mono">/{str(row.slug)}</div>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{str(row.cluster)}</td>
                      <td className="px-4 py-3">
                        {Number(row.price) === 0
                          ? <span className="text-emerald-600 font-semibold">Free</span>
                          : <><span className="font-semibold text-gray-900">${str(row.price)}</span><span className="text-gray-400">/mo</span></>}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-[10px] uppercase tracking-wide rounded px-1.5 py-0.5 ${row.source === 'static' ? 'bg-gray-100 text-gray-500' : 'bg-primary-500/10 text-primary-600'}`}>{str(row.source)}</span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button onClick={() => removeProductRow(row)} disabled={catBusy}
                          className="inline-flex items-center gap-1 text-xs font-semibold text-gray-500 hover:text-red-600 disabled:opacity-50">
                          <Trash2 size={14} /> {row.source === 'static' ? 'Hide' : 'Delete'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Hidden products */}
          {arr(catHidden).length > 0 && (
            <div className="card">
              <h4 className="font-semibold text-gray-900 text-sm mb-2">Hidden products</h4>
              <div className="space-y-1.5">
                {catHidden.map((slug) => (
                  <div key={slug} className="flex items-center gap-2 text-sm">
                    <span className="font-mono text-gray-500">/{str(slug)}</span>
                    <button onClick={() => restoreProduct(slug)} disabled={catBusy}
                      className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-primary-600 hover:underline disabled:opacity-50">
                      <RotateCcw size={13} /> Restore
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
